import React from 'react';
import HelpBadge from './components/HelpBadge';
import GlossaryTerm from './components/GlossaryTerm';
import './RazzmatazzInstallGuide.css';

export type InstallPlatformGuide = {
  platform: string;
  label_en: string;
  label_es: string;
  emoji?: string;
  color?: string;
  meme_en?: string;
  meme_es?: string;
  where_en?: string;
  where_es?: string;
  steps_en?: string[];
  steps_es?: string[];
  tip_en?: string;
  tip_es?: string;
};

export type InstallGuideResponse = {
  ok?: boolean;
  host?: string;
  guide?: {
    headline_en?: string;
    headline_es?: string;
    not_agent_en?: string;
    not_agent_es?: string;
    primary?: InstallPlatformGuide;
    alternatives?: InstallPlatformGuide[];
    detected_cms?: string[];
  };
  platforms?: InstallPlatformGuide[];
  message_en?: string;
  message_es?: string;
  hosting?: { cms?: string[]; confidence?: number };
};

type Props = {
  language?: 'en' | 'es';
  snippet: string;
  host: string;
  siteId: number;
  gatewayBaseUrl: string;
  accessToken: string;
};

const RazzmatazzInstallGuide: React.FC<Props> = ({
  language = 'en',
  snippet,
  host,
  siteId,
  gatewayBaseUrl,
  accessToken,
}) => {
  const spanish = language === 'es';
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [payload, setPayload] = React.useState<InstallGuideResponse | null>(null);
  const [manualId, setManualId] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const activeGuide = React.useMemo(() => {
    if (manualId && payload?.platforms) {
      return payload.platforms.find((row) => row.platform === manualId) || null;
    }
    return payload?.guide?.primary || null;
  }, [manualId, payload]);

  const runDetect = React.useCallback(async () => {
    setBusy(true);
    setError('');
    setManualId('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl.replace(/\/$/, '')}/api/v1/razzmatazz/sites/${siteId}/detect`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const body = (await response.json().catch(() => ({}))) as InstallGuideResponse & { detail?: string };
      if (!response.ok && !body.guide) {
        setError(String(body.detail || (spanish ? 'No se pudo escanear el sitio.' : 'Could not scan the website.')));
        return;
      }
      setPayload(body);
    } catch {
      setError(spanish ? 'No se pudo escanear el sitio.' : 'Could not scan the website.');
    } finally {
      setBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, siteId, spanish]);

  React.useEffect(() => {
    void runDetect();
  }, [runDetect]);

  const copySnippet = React.useCallback(() => {
    void navigator.clipboard?.writeText(snippet);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  }, [snippet]);

  const steps = spanish ? activeGuide?.steps_es : activeGuide?.steps_en;
  const tip = spanish ? activeGuide?.tip_es : activeGuide?.tip_en;
  const meme = spanish ? activeGuide?.meme_es : activeGuide?.meme_en;
  const where = spanish ? activeGuide?.where_es : activeGuide?.where_en;
  const label = spanish ? activeGuide?.label_es : activeGuide?.label_en;
  const headline = spanish ? payload?.guide?.headline_es : payload?.guide?.headline_en;
  const scanNote = spanish ? payload?.message_es : payload?.message_en;

  return (
    <div className="razz-install">
      <div className="razz-install-hero">
        <div className="razz-install-hero-emoji" aria-hidden="true">
          🎯
        </div>
        <div>
          <h5>
            {spanish ? 'Ponga el timbre en su sitio' : 'Hang the doorbell on your website'}
            <HelpBadge label={spanish ? 'Qué es este código' : 'What this code is'}>
              <p>
                {spanish
                  ? 'Es un timbre pequeño. Cada visita toca el timbre. No es el agente de cumplimiento del computador.'
                  : 'It is a tiny doorbell. Each visit rings it. It is not the compliance computer agent.'}
              </p>
            </HelpBadge>
          </h5>
          <p className="razz-install-lede">
            {spanish
              ? `Sitio: ${host}. Primero copiamos el código. Luego seguimos clics sencillos.`
              : `Site: ${host}. First we copy the code. Then we follow easy clicks.`}
          </p>
        </div>
      </div>

      <div className="razz-install-copy-row">
        <button type="button" className="dashboard-btn razz-install-copy" onClick={copySnippet}>
          {copied
            ? spanish
              ? '✓ ¡Copiado! Ahora péguelo donde digan los pasos.'
              : '✓ Copied! Now paste where the steps say.'
            : spanish
              ? '1. Copiar el código mágico'
              : '1. Copy the magic code'}
        </button>
        <button type="button" className="dashboard-btn dashboard-btn-ghost" disabled={busy} onClick={() => void runDetect()}>
          {busy
            ? spanish
              ? 'Escaneando…'
              : 'Scanning…'
            : spanish
              ? 'Volver a escanear el sitio'
              : 'Scan website again'}
        </button>
      </div>

      <textarea className="razz-install-snippet" readOnly rows={6} value={snippet} aria-label={spanish ? 'Código' : 'Code'} />

      {error ? <p className="razz-install-error" role="alert">{error}</p> : null}
      {scanNote ? <p className="razz-install-note" role="status">{scanNote}</p> : null}

      {busy && !activeGuide ? (
        <p className="razz-install-scanning" role="status">
          {spanish
            ? 'Mirando su sitio para adivinar WordPress, Wix, Shopify…'
            : 'Looking at your site to guess WordPress, Wix, Shopify…'}
        </p>
      ) : null}

      {activeGuide ? (
        <article
          className="razz-install-card"
          style={{ borderColor: activeGuide.color || 'var(--razz-line)' }}
        >
          <header className="razz-install-card-head">
            <span className="razz-install-platform-emoji" aria-hidden="true">
              {activeGuide.emoji || '🧩'}
            </span>
            <div>
              <strong>{label}</strong>
              {where ? <p className="razz-muted">{where}</p> : null}
            </div>
          </header>
          {headline ? <p className="razz-install-headline">{headline}</p> : null}
          {meme ? <p className="razz-install-meme">{meme}</p> : null}
          <ol className="razz-install-steps">
            {(steps || []).map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {tip ? (
            <p className="razz-install-tip">
              <span aria-hidden="true">💡</span> {tip}
            </p>
          ) : null}
          <p className="razz-install-gtm-hint">
            {spanish ? (
              <>
                ¿Prefiere{' '}
                <GlossaryTerm
                  term="Google Tag Manager"
                  definition="Una caja de herramientas en la nube donde se pegan scripts del sitio sin tocar cada página."
                >
                  Google Tag Manager
                </GlossaryTerm>
                ? Use la guía alternativa abajo.
              </>
            ) : (
              <>
                Prefer{' '}
                <GlossaryTerm
                  term="Google Tag Manager"
                  definition="A cloud toolbox where website scripts are pasted once, so you do not edit every page."
                >
                  Google Tag Manager
                </GlossaryTerm>
                ? Use an alternate guide below.
              </>
            )}
          </p>
        </article>
      ) : null}

      {(payload?.platforms || []).length ? (
        <label className="razz-install-manual">
          <span>
            {spanish
              ? '¿No es correcto? Elija usted el creador del sitio:'
              : 'Wrong guess? Pick your website builder:'}
          </span>
          <select
            value={manualId || activeGuide?.platform || ''}
            onChange={(event) => setManualId(event.target.value)}
          >
            {(payload?.platforms || []).map((row) => (
              <option key={row.platform} value={row.platform}>
                {(row.emoji ? `${row.emoji} ` : '') + (spanish ? row.label_es : row.label_en)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
};

export default RazzmatazzInstallGuide;
